import {
  AbstractControl,
  AbstractControlOptions,
  AsyncValidatorFn,
  FormArray,
  FormControl,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';
import { EnhancedFormControl } from '../../models/rt-common/form-config';
import { FORM_VALIDATION_ERROR_MSG_MAP } from '../constants/message.constant';
import { MessageItem } from '../services/hint-dialog.service';
import { isNullOrEmpty } from '../utils/common-functions';

/**
 * 進階版 FormGroup
 *
 * @export
 * @class FormGroupEx
 * @extends {FormGroup}
 */
export class FormGroupEx<
  TControl extends {
    [K in keyof TControl]: AbstractControl<any>;
  } = any
> extends FormGroup {
  validationMsgMap = FORM_VALIDATION_ERROR_MSG_MAP;
  private _submitted = false;

  /**
   * 表單是否送出 getter
   */
  get submitted(): boolean {
    return this._submitted;
  }

  /**
   * 表單是否送出 setter
   */
  set submitted(value: boolean) {
    this.setSubmitValue(this, value);
  }

  constructor(
    controls: { [key: string]: AbstractControl } = {},
    validatorOrOpts?:
      | ValidatorFn
      | ValidatorFn[]
      | AbstractControlOptions
      | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
  ) {
    super(controls, validatorOrOpts, asyncValidator);
  }

  /**
   * 重置 FormGroupEx 狀態
   * override FormGroup reset
   *
   * @param {*} [value={}]
   * @param {{ onlySelf?: boolean, emitEvent?: boolean }} [options={}]
   * @memberof FormGroupEx
   */
  reset(
    value: any = {},
    options: { onlySelf?: boolean; emitEvent?: boolean } = {}
  ): void {
    this.submitted = false;
    super.reset(value, options);
  }

  /**
   * 檢核 FormGroupEx
   *
   * @memberof FormGroupEx
   */
  validateAll(): void {
    this.validateAllControls(this);
    this.markAllAsTouched();
  }

  /**
   * 取得控制項檢核錯誤訊息
   *
   * @param {(AbstractControl | null)} control
   * @param {string} [controlName]
   * @return {*}  {MessageItem[]}
   * @memberof FormGroupEx
   */
  getFormValidationErrorMessages(
    control: AbstractControl | null,
    controlName?: string
  ): MessageItem[] {
    if (isNullOrEmpty(control)) {
      return [];
    }

    if (control instanceof FormControl) {
      const errKeys = Object.keys(control?.errors ?? {});
      const category =
        control instanceof FormControlEx ||
        control instanceof EnhancedFormControl
          ? control.displayName
          : controlName;
      const msgItems = errKeys.map((errName) => {
        const msg =
          this.validationMsgMap[
            errName as keyof typeof this.validationMsgMap
          ] ?? errName;
        return { category: category, message: msg } as MessageItem;
      });
      return msgItems;
    }

    let errMsgItems: MessageItem[] = [];
    if (control instanceof FormGroup) {
      const form = control as FormGroup;
      Object.keys(form.controls).forEach((ctrlName, idx) => {
        const ctrl = form.get(ctrlName);
        errMsgItems = errMsgItems.concat(
          this.getFormValidationErrorMessages(ctrl, ctrlName)
        );
      });
    }

    if (control instanceof FormArray) {
      const array = control as FormArray;
      for (let i = 0; i < array.length; i++) {
        const ctrl = array.at(i);
        errMsgItems = errMsgItems.concat(
          this.getFormValidationErrorMessages(ctrl, i.toString())
        );
      }
    }

    return errMsgItems;
  }

  /**
   * 遞迴檢核 FormGroupEx 下所有控制項
   *
   * @private
   * @param {(AbstractControl | null)} target
   * @return {*}  {void}
   * @memberof FormGroupEx
   */
  private validateAllControls(target: AbstractControl | null): void {
    if (isNullOrEmpty(target)) {
      return;
    }
    if (target instanceof FormControl) {
      target.updateValueAndValidity();
      return;
    }
    if (target instanceof FormGroup) {
      const group = target as FormGroup;
      Object.keys(group.controls).forEach((ctrlName) => {
        const ctrl = group.get(ctrlName);
        this.validateAllControls(ctrl);
      });
      return;
    }
    if (target instanceof FormArray) {
      const array = target as FormArray;
      array.controls.forEach((ctrl) => {
        this.validateAllControls(ctrl);
      });
      return;
    }
  }

  /**
   * 遞迴設定表單送出狀態
   *
   * @private
   * @param {(AbstractControl | null)} target
   * @param {boolean} value
   * @return {*}  {void}
   * @memberof FormGroupEx
   */
  private setSubmitValue(target: AbstractControl | null, value: boolean): void {
    if (isNullOrEmpty(target)) {
      return;
    }
    if (target instanceof FormGroupEx) {
      if (target === this) {
        this._submitted = value;
      } else {
        target.submitted = value;
      }
    }
    if (target instanceof FormControl) {
      return;
    }
    if (target instanceof FormGroup) {
      const group = target as FormGroup;
      Object.keys(group.controls).forEach((ctrlName) => {
        const ctrl = group.get(ctrlName);
        this.setSubmitValue(ctrl, value);
      });
      return;
    }
    if (target instanceof FormArray) {
      const array = target as FormArray;
      array.controls.forEach((ctrl) => {
        this.setSubmitValue(ctrl, value);
      });
      return;
    }
  }
}

/**
 * 進階版 FormControl
 *
 * @export
 * @class FormControlEx
 * @extends {FormControl}
 */
export class FormControlEx extends FormControl {
  displayName?: string;
  constructor(
    displayName?: string,
    formState?: any,
    validatorOrOpts?:
      | ValidatorFn
      | ValidatorFn[]
      | AbstractControlOptions
      | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
  ) {
    super(formState, validatorOrOpts, asyncValidator);
    this.displayName = displayName;
  }
}
